<template>
  <div v-if="loading" class="fixed inset-0 bg-white flex flex-col items-center justify-center z-50">
    <!-- Loading Component -->
    <div class="flex flex-col items-center">
      <h1 class="text-4xl font-bold text-gray-800 mt-4" style="font-family: 'Arial Rounded MT Bold', sans-serif;">
        Chargement des gestionnaires
      </h1>
    </div>
  </div>

  <div v-else class="inset-0 bg-white z-50" data-component="Attribution">
    <!-- Close Button -->
    <div class="is-flex has-item-ended">
      <CloseButton :referer="referer" />
    </div>

    <!-- Title -->
    <div class="is-flex has-item-centered px-12">
      <h1 
        v-if="lots.length === 1"
        class="attribution-title"
      >
        Attribuez la prochaine visite du lot {{ lots[0]?.nsil }}
      </h1>
      <h1 
        v-else
        class="attribution-title"
        :title="`Attribuez lots: ${lots.map(lot => lot.nsil).join(' ,')}`"
      >
        Attribuez les lots
      </h1>
    </div>

    <!-- Team Selection -->
    <div class="is-flex has-item-centered px-12">
      <div class="is-flex has-item-aligned-center is-bordered-top">
        <label 
          for="select-team" 
          class="attribution-label"
        >
          Groupe
        </label>
        <div class="field">
          <div class="control">
            <div class="select is-medium">
              <select 
                id="select-team" 
                :value="selectedTeam" 
                @change="handleTeamChange"
              >
                <option value="">{{ '' }}</option>
                <option 
                  v-for="team in teams" 
                  :key="`team-${team.value}`"
                  :value="team.value"
                >
                  {{ team.label }}
                </option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Manager Selection -->
    <div v-if="isTeamSelected" class="is-flex has-item-centered px-12">
      <div class="is-flex has-item-aligned-baseline is-bordered-top">
        <label 
          for="manager" 
          class="attribution-label"
        >
          Gestionnaire
        </label>
        <div class="is-flex has-direction-column">
          <div 
            v-for="manager in managers" 
            :key="`manager-${manager.value}`"
          >
            <RadioButton
              :label="manager.label"
              :name="'manager'"
              :id="`manager-${manager.value}`"
              :value="manager.value"
              :checked="selectedManager === manager.value"
              @change="handleManagerChange(manager.value)"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Submit Button -->
    <div class="is-flex has-item-centered px-12">
      <Button 
        size="large" 
        :disabled="!isValid || isSubmitting"
        @click="sendAttribution"
      >
        {{ isSubmitting ? 'Attribution en cours...' : 'Valider' }}
      </Button>
    </div>
  </div>
</template>

<script>
import CloseButton from '../Widget/CloseButton.vue'
import Button from '../Widget/Button.vue'
import RadioButton from '../Widget/RadioButton.vue'
import dexieService from '../../services/dexieService'
import mutationService from '../../services/mutationService'

/* eslint-disable vue/multi-word-component-names */
/* eslint-disable vue/no-reserved-component-names */
/* eslint-disable */
export default {
  name: 'Attribution',
  components: {
    CloseButton,
    Button,
    RadioButton
  },
  data() {
    return {
      loading: true,
      lots: [],
      teams: [],
      managers: [],
      allManagers: [], // Tous les managers pour filtrage
      selectedTeam: '',
      selectedManager: '',
      referer: '/visites',
      isSubmitting: false
    }
  },
  computed: {
    isTeamSelected() {
      return this.selectedTeam !== ''
    },
    isValid() {
      // Validation : un gestionnaire sélectionné
      return this.selectedManager !== ''
    }
  },
  async mounted() {
      await this.initializeData()
  },
  methods: {
    async initializeData() {
      try {
        // Récupérer l'URL précédente depuis l'historique du navigateur
        this.referer = this.$router.options.history.state.back || '/visites'

        
        // Récupérer les query parameters
        const lots = this.$route.query['lots[]']
        const teamId = this.$route.query.team
        const managerId = this.$route.query.manager
        
        
        // Traiter les lots (peut être un string ou un array)
        if (lots) {
          const lotIds = Array.isArray(lots) ? lots : [lots]
          // Récupérer les données réelles des lots depuis Dexie
          const lotsPromises = lotIds.map(id => dexieService.getProperty(id))
          const lotsData = await Promise.all(lotsPromises)
          this.lots = lotsData.filter(lot => lot !== null) // Filtrer les lots non trouvés
        }
        
        // Charger les équipes et gestionnaires depuis Dexie
        await this.loadTeamsAndManagers()
        
        // Pré-sélectionner l'équipe et le gestionnaire si fournis
        if (teamId) {
          this.selectedTeam = teamId
          // Filtrer les managers pour cette équipe
          await this.filterManagersByTeam(teamId)
        }
        if (managerId) {
          this.selectedManager = managerId
        }
        
      } catch (error) {
        console.error('Erreur lors de l\'initialisation:', error)
      } finally {
        this.loading = false
      }
    },

    async loadTeamsAndManagers() {
      try {
        
        // Charger les équipes depuis Dexie
        const teamsData = await dexieService.teams().toArray()
        
        // Formater les équipes pour le select
        this.teams = teamsData
          .filter(team => team.enabled !== false) // Filtrer les équipes actives
          .map(team => ({
            value: team.id,
            label: team.name
          }))
          .sort((a, b) => a.label.localeCompare(b.label)) // Tri alphabétique
        
        
        // Charger tous les managers depuis Dexie
        const managersData = await dexieService.users().toArray()
        
        // Stocker tous les managers pour le filtrage
        this.allManagers = managersData
          .filter(user => user.enabled !== false) // Filtrer les utilisateurs actifs
          .map(user => ({
            value: user.id,
            label: user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim(),
            teamId: user.team?.id
          }))
          .sort((a, b) => a.label.localeCompare(b.label)) // Tri alphabétique
        
        
        // Si aucune équipe n'est sélectionnée, afficher tous les managers
        if (!this.selectedTeam) {
          this.managers = [...this.allManagers]
          // Ajouter l'option "Aucun"
          this.managers.push({ value: '', label: 'Aucun', teamId: null })
        }
        
      } catch (error) {
        console.error('Erreur lors du chargement des teams et managers:', error)
        
        // En cas d'erreur, utiliser des données de fallback
        this.teams = []
        this.managers = [{ value: '', label: 'Aucun', teamId: null }]
      }
    },

    async filterManagersByTeam(teamId) {
      try {
        
        if (!teamId) {
          // Si aucune équipe sélectionnée, afficher tous les managers
          this.managers = [...this.allManagers]
        } else {
          // Filtrer les managers par équipe et trier alphabétiquement
          this.managers = this.allManagers
            .filter(manager => manager.teamId === teamId)
            .sort((a, b) => a.label.localeCompare(b.label))
        }
        
        // Toujours ajouter l'option "Aucun" à la fin
        this.managers.push({ value: '', label: 'Aucun', teamId: null })
        
        
      } catch (error) {
        console.error('Erreur lors du filtrage des managers:', error)
      }
    },
    
    async handleTeamChange(event) {
      this.selectedTeam = event.target.value
      // Reset manager selection when team changes
      this.selectedManager = ''
      
      // Filtrer les managers pour la nouvelle équipe
      await this.filterManagersByTeam(this.selectedTeam)
    },
    
    handleManagerChange(value) {
      this.selectedManager = value
    },
    
    /**
     * Fonction sendAttribution qui appelle le service mutation.js
     * - Si l'API est accessible : met à jour le technicalManager + IndexedDB
     * - Sinon : met à jour IndexedDB avec flag "toSync" à 1
     */
    async sendAttribution() {
      if (!this.isValid || this.isSubmitting) {
        return
      }

      this.isSubmitting = true

      try {
        // Préparer les IDs des lots
        const lotIds = this.lots.map(lot => lot.id)
        
        // Utiliser le service mutation pour traiter les attributions
        const result = await mutationService.processMultipleAttributions(
          lotIds,
          this.selectedManager || null, // Gestionnaire sélectionné (peut être null pour "Aucun")
          this.selectedTeam || null     // Équipe sélectionnée (peut être null)
        )


        if (result.success) {
          // Afficher un message de succès
          const message = result.synced 
            ? `Attribution réussie pour ${result.successCount}/${result.totalCount} lots (synchronisé avec l'API)`
            : `Attribution sauvegardée localement pour ${result.successCount}/${result.totalCount} lots (sera synchronisée plus tard)`
          
          
          // Optionnel : afficher une notification à l'utilisateur
          this.showSuccessMessage(message, result.synced)
          
          // Retourner à la page précédente après un court délai
          setTimeout(() => {
            this.goBack()
          }, 1500)
          
        } else {
          // Gestion des erreurs
          const errorMessage = `Erreur lors de l'attribution: ${result.error || 'Erreur inconnue'}`
          console.error(errorMessage)
          this.showErrorMessage(errorMessage)
        }

      } catch (error) {
        console.error('Erreur lors de l\'attribution:', error)
        this.showErrorMessage(`Erreur lors de l'attribution: ${error.message}`)
      } finally {
        this.isSubmitting = false
      }
    },

    /**
     * Affiche un message de succès à l'utilisateur
     */
    showSuccessMessage(message, synced) {
      // Pour l'instant, utiliser console.log
      // TODO: Implémenter un système de notifications/toasts
      
      // Optionnel: changer temporairement le texte du bouton
      if (synced) {
        // Attribution synchronisée avec l'API
      } else {
        // Attribution sauvegardée localement
      }
    },

    /**
     * Affiche un message d'erreur à l'utilisateur
     */
    showErrorMessage(message) {
      // Pour l'instant, utiliser console.error
      // TODO: Implémenter un système de notifications/toasts
      console.error('❌ Erreur:', message)
      
      // Optionnel: afficher une alerte simple
      if (typeof window !== 'undefined' && window.alert) {
        window.alert(message)
      }
    },
    
    goBack() {
      if (this.referer && this.referer !== '/visites') {
        this.$router.push(this.referer)
      } else {
        this.$router.push('/visites')
      }
    }
  }
}
</script>

<style scoped>
@import './Attribution.scss';
</style>
